import { useLiveQuery } from 'dexie-react-hooks'
import {
  INCIDENT_CLOSURE_MAX, INCIDENT_DESCRIPTION_MAX, INCIDENT_INVOLVED_MAX, INCIDENT_KINDS, INCIDENT_NOTE_MAX, INCIDENT_SEVERITIES,
  type IncidentAddActionV1, type IncidentCloseV1, type IncidentDetail, type IncidentKind, type IncidentLine, type IncidentReportV1, type IncidentSeverity, type IncidentStatus,
} from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { isOpen } from '../sync/projectors.ts'
import type { MembershipRow, PersonRow } from './people.ts'

/** M16-G, the incident register (docs/M16.md G): the screens' pure rules, with their tests (data/incidents.test.ts), and the two live reads of what THIS device
 *  holds of a report it sent (the stub, never the text: sync/projectors.incidents.ts). Everything the register shows is read online through the audited
 *  `api.read_incidents` / `api.read_incident` and kept in the page's state only (ADR-119: kept on no device). */
export { INCIDENT_KINDS, INCIDENT_SEVERITIES, INCIDENT_DESCRIPTION_MAX, INCIDENT_CLOSURE_MAX, INCIDENT_NOTE_MAX, INCIDENT_INVOLVED_MAX }
export type { IncidentDetail, IncidentKind, IncidentLine, IncidentSeverity, IncidentStatus }

/** Code points, as the server counts them (a text of 2000 `é` is 2000, never 4000 bytes). */
export const codePoints = (s: string): number => [...s].length

type T = (k: string, o?: Record<string, unknown>) => string
/** The words of each kind, severity and status (literal keys, so i18nKeys.test.ts holds each one to the catalogue). */
export function kindText(t: T, k: string): string {
  switch (k) {
    case 'injury': return t('incidents.kind.injury')
    case 'near_miss': return t('incidents.kind.near_miss')
    case 'damage': return t('incidents.kind.damage')
    case 'environment': return t('incidents.kind.environment')
    case 'chemical': return t('incidents.kind.chemical')
    case 'other': return t('incidents.kind.other')
    default: return k
  }
}
export function severityText(t: T, s: string): string {
  switch (s) {
    case 'low': return t('incidents.severity.low')
    case 'medium': return t('incidents.severity.medium')
    case 'high': return t('incidents.severity.high')
    default: return s
  }
}
export function statusText(t: T, s: string): string {
  switch (s) {
    case 'open': return t('incidents.status.open')
    case 'under_review': return t('incidents.status.under_review')
    case 'closed': return t('incidents.status.closed')
    default: return s
  }
}
export const SEVERITY_TONE: Record<IncidentSeverity, 'red' | 'amber' | 'gray'> = { high: 'red', medium: 'amber', low: 'gray' }

// ---- the report ------------------------------------------------------------------------------------------------------------------------------------------
export interface ReportForm {
  kind: IncidentKind | ''; severity: IncidentSeverity | ''; description: string
  /** '' = no area, no machine */
  areaId: string; assetId: string
  involved: readonly string[]
  /** the instant it happened when not now (the page converts the typed local moment with the site's zone) */
  occurredAt?: string
}
export type ReportProblem = 'kind' | 'severity' | 'description' | 'descriptionLong' | 'involvedMany' | 'when' | null
/** What keeps the form from sending, first missing first (the button says it beside itself: M23 item 6). Release b review (finding 6): a moment typed in the
 *  future (a mis-picked day) is held here, against `now` (the page's clock, ms): sent, the server refused it INCIDENT_DATE_INVALID and the salvage kept the report
 *  with its time replaced and flagged "Auteur à vérifier", while the reporter read "Signalement reçu". */
export function reportProblem(f: ReportForm, now?: number): ReportProblem {
  if (!f.kind) return 'kind'
  if (!f.severity) return 'severity'
  if (f.description.trim() === '') return 'description'
  if (codePoints(f.description.trim()) > INCIDENT_DESCRIPTION_MAX) return 'descriptionLong'
  if (new Set(f.involved).size > INCIDENT_INVOLVED_MAX) return 'involvedMany'
  if (f.occurredAt && now !== undefined && Date.parse(f.occurredAt) > now) return 'when'
  return null
}
/** The payload of incident.report@1: the person acting is its reporter (the server refuses anything else); an absent area, machine or people are not sent. */
export function buildReportPayload(incidentId: string, personId: string, f: ReportForm): IncidentReportV1 {
  if (!f.kind || !f.severity) throw new Error('incomplete report')
  const involved = [...new Set(f.involved)]
  return {
    incident_id: incidentId, person_id: personId, kind: f.kind, severity: f.severity, description: f.description.trim(),
    ...(f.areaId ? { area_id: f.areaId } : {}), ...(f.assetId ? { asset_id: f.assetId } : {}), ...(involved.length ? { involved_person_ids: involved } : {}),
    ...(f.occurredAt ? { occurred_at: f.occurredAt } : {}),
  }
}

// ---- the register ----------------------------------------------------------------------------------------------------------------------------------------
/** The days the register asks for: one calendar year of the club (the server takes at most 366 days; every incident not closed comes whatever the days). */
export const yearRange = (year: number): { from: string; to: string } => ({ from: `${year}-01-01`, to: `${year}-12-31` })
/** The register as the page shows it: what is not closed first (the oldest first: it has waited longest), then the closed of the year, newest first. */
export function registerSections(lines: readonly IncidentLine[]): { open: IncidentLine[]; closed: IncidentLine[] } {
  const open = lines.filter((l) => l.status !== 'closed').sort((a, b) => a.occurred_at.localeCompare(b.occurred_at) || a.id.localeCompare(b.id))
  const closed = lines.filter((l) => l.status === 'closed').sort((a, b) => b.occurred_at.localeCompare(a.occurred_at) || a.id.localeCompare(b.id))
  return { open, closed }
}

/** Who may be made responsible (the server's rule, INCIDENT_ASSIGNEE_INVALID): a living human of the club with a role at the site now. From the mirror. */
export function responsibleOptions(persons: readonly PersonRow[], memberships: readonly MembershipRow[], siteId: string): Array<{ id: string; name: string }> {
  const atSite = new Set(memberships.filter((m) => m.site_id === siteId && isOpen(m)).map((m) => m.person_id))
  return persons.filter((p) => p.kind === 'human' && p.employment_state === 'active' && atSite.has(p.id)).map((p) => ({ id: p.id, name: p.display_name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
}

/** A linked job neither done nor cancelled (the server's close rule: INCIDENT_ACTIONS_OPEN unless a reason is given). */
export const isActionOpen = (a: Pick<IncidentDetail['actions'][number], 'job_id' | 'job_state'>): boolean => !!a.job_id && a.job_state !== 'done' && a.job_state !== 'cancelled'
export const openActions = (d: Pick<IncidentDetail, 'actions'>): number => d.actions.filter(isActionOpen).length

export interface ActionForm { jobId: string; note: string; dueOn: string }
export type ActionProblem = 'empty' | 'noteLong' | null
export function actionProblem(f: ActionForm): ActionProblem {
  if (!f.jobId && f.note.trim() === '') return 'empty'
  if (codePoints(f.note.trim()) > INCIDENT_NOTE_MAX) return 'noteLong'
  return null
}
export function buildActionPayload(actionId: string, incidentId: string, f: ActionForm): IncidentAddActionV1 {
  const note = f.note.trim()
  return { action_id: actionId, incident_id: incidentId, ...(f.jobId ? { job_id: f.jobId } : {}), ...(note ? { note } : {}), ...(f.dueOn ? { due_on: f.dueOn } : {}) }
}
/** The jobs that may follow an incident: not cancelled, planned from the incident's day on, not linked yet; the incident's own area first. */
export function followUpJobs<J extends { id: string; state: string; scheduled_local_date: string }>(jobs: readonly J[], jobAreaIds: (jobId: string) => readonly string[],
  d: Pick<IncidentDetail, 'occurred_local_date' | 'area_id' | 'actions'>, titleOf: (j: J) => string): Array<{ id: string; title: string; date: string; onArea: boolean }> {
  const linked = new Set(d.actions.map((a) => a.job_id).filter(Boolean))
  return jobs.filter((j) => j.state !== 'cancelled' && j.scheduled_local_date >= d.occurred_local_date && !linked.has(j.id))
    .map((j) => ({ id: j.id, title: titleOf(j), date: j.scheduled_local_date, onArea: !!d.area_id && jobAreaIds(j.id).includes(d.area_id) }))
    .sort((a, b) => Number(b.onArea) - Number(a.onArea) || a.date.localeCompare(b.date) || a.title.localeCompare(b.title))
}

export interface CloseForm { note: string; reason: string }
export type CloseProblem = 'note' | 'noteLong' | 'reason' | 'reasonLong' | null
export function closeProblem(f: CloseForm, d: Pick<IncidentDetail, 'actions'>): CloseProblem {
  if (f.note.trim() === '') return 'note'
  if (codePoints(f.note.trim()) > INCIDENT_CLOSURE_MAX) return 'noteLong'
  if (openActions(d) > 0 && f.reason.trim() === '') return 'reason'
  if (codePoints(f.reason.trim()) > INCIDENT_NOTE_MAX) return 'reasonLong'
  return null
}
/** The reason is sent only when a linked job is still open (the server keeps it only then). */
export function buildClosePayload(incidentId: string, f: CloseForm, d: Pick<IncidentDetail, 'actions'>): IncidentCloseV1 {
  const reason = f.reason.trim()
  return { incident_id: incidentId, closure_note: f.note.trim(), ...(openActions(d) > 0 && reason ? { open_actions_reason: reason } : {}) }
}

// ---- what this device holds of a report it sent (the stub, never the text) -------------------------------------------------------------------------------
const WAITING = new Set(['queued', 'sending', 'transient', 'blocked'])
/** How many reports this device still has to send: a stub counts only through its waiting outbox row (sync/transient.ts removes it once answered). */
export function useIncidentsWaiting(): number {
  const { db } = useClub()
  return useLiveQuery(async () => {
    let n = 0
    for (const s of await db.incident_stub.toArray()) { const r = await db.outbox.get(s.command_id); if (r && WAITING.has(r.status)) n++ }
    return n
  }, [db], 0)
}
export type ReportState = { state: 'waiting' } | { state: 'received' } | { state: 'refused'; code: string | null } | { state: 'unknown' }
/** The answer to one report, from what the device keeps: its waiting row, the archive of applied commands (ids only), or its refused row (its id only). */
export function useReportState(commandId: string | null): ReportState {
  const { db } = useClub()
  return useLiveQuery(async (): Promise<ReportState> => {
    if (!commandId) return { state: 'unknown' }
    const row = await db.outbox.get(commandId)
    if (row && WAITING.has(row.status)) return { state: 'waiting' }
    if (row && (row.status === 'rejected' || row.status === 'conflict' || row.status === 'error')) return { state: 'refused', code: row.server_code ?? null }
    if (await db.archive.get(commandId)) return { state: 'received' }
    return { state: 'unknown' }
  }, [db, commandId], { state: 'unknown' })
}
